import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import nl from "./nl";
import en from "./en";

// Les textes du site sont écrits en français : la phrase française sert de clé.
// nl.js et en.js donnent la traduction de chaque phrase ; s'il en manque une,
// on affiche le français.
export const LANGS = [
  { code: "fr", label: "FR", name: "Français" },
  { code: "nl", label: "NL", name: "Nederlands" },
  { code: "en", label: "EN", name: "English" },
];

const DICTS = { nl, en };
const LOCALES = { fr: "fr-BE", nl: "nl-BE", en: "en-GB" };

export const translate = (lang, key, vars) => {
  const k = String(key).replace(/[\u00a0\u202f]/g, " ");
  let s = (DICTS[lang] && DICTS[lang][k]) || key;
  if (vars) {
    Object.keys(vars).forEach((k) => {
      s = s.split(`{${k}}`).join(vars[k]);
    });
  }
  return s;
};

export const localeOf = (lang) => LOCALES[lang] || LOCALES.fr;

const detectLang = () => {
  try {
    const saved = localStorage.getItem("ts_lang");
    if (saved && LOCALES[saved]) return saved;
  } catch {
    /* stockage indisponible */
  }
  const nav = (typeof navigator !== "undefined" && (navigator.language || "")).toLowerCase();
  if (nav.startsWith("nl")) return "nl";
  if (nav.startsWith("en")) return "en";
  return "fr";
};

// Valeur par défaut = français (utile pour les tests sans fournisseur)
const LanguageContext = createContext({
  lang: "fr",
  setLang: () => {},
  locale: "fr-BE",
  t: (key, vars) => translate("fr", key, vars),
});

export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(detectLang);

  const setLang = useCallback((l) => {
    if (!LOCALES[l]) return;
    setLangState(l);
    try {
      localStorage.setItem("ts_lang", l);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const value = useMemo(
    () => ({ lang, setLang, locale: localeOf(lang), t: (key, vars) => translate(lang, key, vars) }),
    [lang, setLang]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLang = () => useContext(LanguageContext);

/** Sélecteur FR | NL | EN */
export const LanguageSwitch = ({ className = "", dark = false }) => {
  const { lang, setLang, t } = useLang();
  return (
    <div
      role="group"
      aria-label={t("Langue")}
      className={`inline-flex items-center rounded-full p-0.5 text-xs font-bold ${
        dark ? "bg-white/10" : "bg-lin"
      } ${className}`}
    >
      {LANGS.map((l) => {
        const active = l.code === lang;
        return (
          <button
            key={l.code}
            type="button"
            onClick={() => setLang(l.code)}
            title={l.name}
            aria-pressed={active}
            lang={l.code}
            className={`px-2 py-1 rounded-full transition-colors ${
              active
                ? "bg-prune text-white shadow-sm"
                : dark
                ? "text-white/80 hover:text-white"
                : "text-stone-600 hover:text-prune"
            }`}
          >
            {l.label}
          </button>
        );
      })}
    </div>
  );
};
